import { useEffect, useState, useRef, useMemo } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useLocalization } from "@/hooks/useLocalization";
import { Header } from "@/components/Header";
import { BountyCard } from "@/components/BountyCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Target, Plus, Camera, Eye, DollarSign, Wifi, Shield, Lock, Star, ArrowRight, Clock, CheckCircle2, Globe, MessageCircleQuestion, Search, Video, ShieldCheck, MapPin, BadgeCheck } from "lucide-react";
import { bountyApi } from "@/services/api";
import { ROUTES, BOUNTY_CONFIG, INTRO_OFFER_ENABLED, INTRO_OFFER_CONFIG, INTRO_OFFER_TERMS } from "@/config/constants";
import { trackFunnelEvent } from "@/lib/funnelAnalytics";
import type { BountyRow } from "@/types";
import { toast } from "sonner";
import { getServerNow, formatTimeRemaining } from "@/lib/serverTime";
import { useStore } from "@/store";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import AdminSecureAccess from "@/components/AdminSecureAccess";
import { isExpired, getActualBountyStatus } from "@/lib/utils";
import { COUNTRY_NAMES, countryToFlag } from "@/lib/countryFlags";
import { motion } from "framer-motion";
import { FadeIn, StaggerContainer, StaggerItem } from "@/components/motion";

const Index = () => {
  const { user } = useAuth();
  const { refetchProfile } = useStore();
  const { formatCurrency } = useLocalization();
  const [searchParams] = useSearchParams();
  const [bounties, setBounties] = useState<BountyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [filter, setFilter] = useState<"open" | "all">("open");
  const [countryFilter, setCountryFilter] = useState<string>("all");
  const [isLive, setIsLive] = useState(false);
  const lastUpdateRef = useRef(0);
  const [showAdminAccess, setShowAdminAccess] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showCookiePolicy, setShowCookiePolicy] = useState(false);
  const [showDataProcessing, setShowDataProcessing] = useState(false);
  const [showDataRights, setShowDataRights] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash === '#feed') document.getElementById('feed')?.scrollIntoView();
  }, [hash]);

  // Handle Stripe payment success/cancel
  useEffect(() => {
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');
    const sessionId = searchParams.get('session_id');

    if (success && sessionId && user) {
      // Payment was successful - activate Pro
      handleSuccessfulPayment(sessionId);
    } else if (cancelled) {
      toast.info("Payment cancelled", { description: "Your subscription was not processed" });
    }
  }, [searchParams, user]);

  const handleSuccessfulPayment = async (sessionId: string) => {
    try {
      // Refresh profile to pick up server-side Pro upgrade from webhook
      await refetchProfile();

      toast.success("Welcome to Pro!", { description: "Your Sheriff's Badge has been activated" });
    } catch (error: any) {
      // Webhook may still be processing — don't show error
    }
  };

  useEffect(() => {
    document.title = "BountyClaimer — Get human tasks done with proof";
    const meta = document.querySelector('meta[name="description"]');
    const desc = "Post a task, fund the reward, and review proof before payout. Free: $2–$10,000 per hunter, 7-day listings. Pro: $0.50–$10,000, up to 30 days. Fees apply.";
    if (meta) meta.setAttribute("content", desc);
    else {
      const m = document.createElement("meta");
      m.name = "description"; m.content = desc; document.head.appendChild(m);
    }
  }, []);

  useEffect(() => {
    const loadBounties = async () => {
      setLoading(true);
      setLoadFailed(false);
      console.log('[Index] Loading bounties with filter:', filter);
      const { data, error } = await bountyApi.getBounties(filter);
      console.log('[Index] Bounties loaded:', data?.length || 0, 'with filter:', filter);
      console.log('[Index] Bounty titles:', data?.map(b => b.title));
      if (error) {
        setLoadFailed(true);
        // Only show error if it's NOT a 401 (unauthorized) error
        // 401 means user needs to log in, which is expected for some features
        if (!error.message?.includes('401') && !error.message?.includes('JWT')) {
          toast.error("Failed to load bounties");
          console.error("Bounty load error:", error);
        } else {
          console.log("Auth required for bounties - user should log in");
        }
        setBounties([]);
      } else {
        console.log('[Index] Setting bounties state:', data?.length || 0);
        // Sort by priority: featured > high priority > normal
        const sorted = (data ?? []).sort((a, b) => {
          // PHOENIX: Check featured status using isExpired() for consistency
          // Featured bounties must: have is_featured=true, featured_expires_at not expired, AND bounty itself not expired
          const aExpired = getActualBountyStatus(a.status, a.expires_at) === 'expired';
          const bExpired = getActualBountyStatus(b.status, b.expires_at) === 'expired';
          const aFeatured = a.is_featured && a.featured_expires_at && !isExpired(a.featured_expires_at) && !aExpired;
          const bFeatured = b.is_featured && b.featured_expires_at && !isExpired(b.featured_expires_at) && !bExpired;
          if (aFeatured && !bFeatured) return -1;
          if (!aFeatured && bFeatured) return 1;
          // Then sort by created_at (newest first)
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        });
        setBounties(sorted);
      }
      setLoading(false);
    };
    
    loadBounties();
    
    const unsubscribe = bountyApi.subscribeToBounties(() => {
      // PHOENIX: Use SERVER time for consistent throttling across all devices
      const now = getServerNow();
      if (now - lastUpdateRef.current < 2000) return; // Throttle to 2s
      lastUpdateRef.current = now;
      
      console.log("🔔 Real-time update: Bounty changed! Current bounties:", bounties.length);
      setIsLive(true);
      loadBounties();
      setTimeout(() => setIsLive(false), 2000);
    });
    return () => unsubscribe();
  }, [filter]);

  // Spotlight bounties: featured and not expired — rendered as large wanted posters
  const featuredBounties = useMemo(() => {
    return bounties
      .filter((b) => {
        const expired = getActualBountyStatus(b.status, b.expires_at) === 'expired';
        return b.is_featured && b.featured_expires_at && !isExpired(b.featured_expires_at) && !expired;
      })
      .slice(0, 2);
  }, [bounties]);

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Modern Hero */}
        <section className="relative overflow-hidden">
          {/* Decorative background elements */}
          <div className="absolute inset-0 pointer-events-none">
            <div
              aria-hidden
              className="absolute -top-32 right-[-10%] h-[480px] w-[640px] rounded-full bg-[radial-gradient(closest-side,hsl(45_95%_55%/0.22),transparent)] blur-2xl dark:bg-[radial-gradient(closest-side,hsl(42_95%_55%/0.12),transparent)]"
            />
            <div
              aria-hidden
              className="absolute top-40 left-[-12%] h-[420px] w-[520px] rounded-full bg-[radial-gradient(closest-side,hsl(25_60%_45%/0.10),transparent)] blur-2xl"
            />
          </div>

          <div className="container relative pt-12 pb-14 md:pt-20 md:pb-20 lg:grid lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-12">
            <div className="text-center lg:text-left">
              <FadeIn direction="down" delay={0.05}>
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[hsl(45_95%_55%)]/15 border border-[hsl(45_90%_50%)]/30 text-[11px] sm:text-xs font-semibold tracking-wide text-[hsl(32_85%_32%)] dark:text-[hsl(45_95%_60%)]">
                    <Target className="h-3.5 w-3.5" /> Human-verified tasks
                  </div>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-600/10 border border-emerald-600/25 text-[11px] sm:text-xs font-semibold tracking-wide text-emerald-700 dark:text-emerald-400">
                    <Shield className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Secured by Armadillo</span>
                    <span className="sm:hidden">Armadillo Secured</span>
                    <Lock className="h-3 w-3" />
                  </div>
                </div>
              </FadeIn>
              <FadeIn delay={0.15}>
                <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tight leading-[1.05] max-w-2xl mx-auto lg:mx-0 mb-6">
                  Get your task done.{' '}
                  <span className="text-shimmer">See the proof.</span>
                </h1>
              </FadeIn>
              <FadeIn delay={0.25}>
                <p className="text-base sm:text-lg md:text-xl text-muted-foreground max-w-xl mx-auto lg:mx-0">
                  Need a local photo, a website check, or a real-world answer? Fund a reward — a real person delivers proof you approve before payout.
                </p>
              </FadeIn>
              <FadeIn delay={0.35}>
                <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-4 mt-8 px-4 lg:px-0">
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 17 }}>
                    <Button asChild size="lg" className="shine-sweep h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg bg-gradient-to-r from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] hover:from-[hsl(45_90%_50%)] hover:to-[hsl(35_84%_42%)] text-[hsl(24_45%_12%)] font-bold border-none shadow-lg hover:shadow-xl transition-all duration-300">
                      <Link to={ROUTES.POST_BOUNTY} onClick={() => trackFunnelEvent('entry_intent', { intent: 'post', source: 'landing_hero' })}><Plus className="h-4 w-4 sm:h-5 sm:w-5 mr-2" /> Post a task</Link>
                    </Button>
                  </motion.div>
                  <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} transition={{ type: "spring", stiffness: 400, damping: 17 }}>
                    <Button asChild size="lg" variant="outline" className="h-12 sm:h-14 px-6 sm:px-8 text-base sm:text-lg">
                      <a href="#feed" onClick={() => trackFunnelEvent('entry_intent', { intent: 'earn', source: 'landing_hero' })}><Eye className="h-4 w-4 sm:h-5 sm:w-5 mr-2" /> Earn rewards</a>
                    </Button>
                  </motion.div>
                </div>
              </FadeIn>
              <FadeIn delay={0.42}>
                <div className="mt-6 px-4 lg:px-0 flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-x-4 gap-y-1.5 text-sm text-muted-foreground">
                  <span>Start your draft without an account — sign up when you are ready to fund and publish.</span>
                  <a
                    href="#pricing"
                    className="font-semibold text-primary underline decoration-dotted underline-offset-4 hover:decoration-solid transition-colors whitespace-nowrap"
                  >
                    From {formatCurrency(BOUNTY_CONFIG.FREE_MIN_REWARD_CENTS)} per hunter · see pricing
                  </a>
                </div>
              </FadeIn>
              <FadeIn delay={0.5}>
                <div className="mt-7 px-4 lg:px-0 flex flex-wrap items-center justify-center lg:justify-start gap-x-5 gap-y-2 text-sm">
                  {[
                    "Pay only when you approve the proof",
                    "Rewards escrowed until payout",
                    "No account needed to draft",
                  ].map((t) => (
                    <span key={t} className="inline-flex items-center gap-1.5 text-muted-foreground">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t}
                    </span>
                  ))}
                </div>
              </FadeIn>
            </div>

            {/* Proof-flow visual */}
            <FadeIn delay={0.25} className="mt-12 lg:mt-0">
              <div className="relative mx-auto max-w-md lg:max-w-none">
                <div aria-hidden className="absolute -inset-4 rounded-[2rem] bg-gradient-to-br from-[hsl(45_95%_55%)]/15 via-transparent to-transparent blur-xl" />
                {/* Task card */}
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  className="relative rounded-2xl border border-border bg-card p-5 shadow-lg"
                >
                  <div className="flex items-center justify-between gap-3 mb-3">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-secondary px-2.5 py-1 text-xs font-medium">
                      <Camera className="h-3.5 w-3.5 text-[hsl(38_80%_42%)]" /> Photo proof
                    </span>
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock className="h-3.5 w-3.5" /> 3 days left
                    </span>
                  </div>
                  <h3 className="font-bold text-lg leading-snug mb-1.5">Photograph the mural on 5th &amp; Main</h3>
                  <p className="text-sm text-muted-foreground">Show the full wall and the street sign in one shot. Two photos minimum.</p>
                  <div className="mt-4 flex items-center justify-between border-t border-border pt-3">
                    <span className="reward-badge text-sm">$12.00</span>
                    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3.5 w-3.5" /> St. George's
                    </span>
                  </div>
                </motion.div>
                {/* Proof submitted card */}
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  className="relative -mt-4 ml-6 sm:ml-10 rounded-2xl border border-border bg-card p-4 shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[hsl(45_95%_55%)] to-[hsl(32_85%_42%)] text-[hsl(24_45%_12%)]">
                      <Camera className="h-6 w-6" />
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold">Proof submitted</div>
                      <div className="text-xs text-muted-foreground">2 photos · awaiting your review</div>
                    </div>
                    <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-amber-500/15 px-2.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400 whitespace-nowrap">
                      In review
                    </span>
                  </div>
                </motion.div>
                {/* Approved card */}
                <motion.div
                  whileHover={{ y: -4 }}
                  transition={{ type: "spring", stiffness: 300, damping: 24 }}
                  className="relative -mt-3 mr-6 sm:mr-10 rounded-2xl border border-border bg-card p-4 shadow-lg"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-5 w-5" />
                    </span>
                    <div>
                      <div className="text-sm font-semibold">Proof approved</div>
                      <div className="text-xs text-muted-foreground">$12.00 paid to the hunter</div>
                    </div>
                    <span className="ml-auto reward-badge text-xs whitespace-nowrap">Paid out</span>
                  </div>
                </motion.div>
                {/* Floating trust pill */}
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                  className="absolute -top-5 right-2 sm:-right-3 inline-flex items-center gap-1.5 rounded-full border border-[hsl(45_90%_50%)]/40 bg-card px-3 py-1.5 text-xs font-bold shadow-md"
                >
                  <BadgeCheck className="h-4 w-4 text-[hsl(38_85%_45%)]" /> 100% proof-approved payouts
                </motion.div>
              </div>
            </FadeIn>
          </div>
        </section>

        {/* Benefit stats strip */}
        <section className="container pb-14 md:pb-20">
          <FadeIn inView className="max-w-5xl mx-auto">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: BadgeCheck, title: "100% proof-approved payouts", text: "Money only moves after a human approves the proof." },
                { icon: ShieldCheck, title: "Escrowed rewards", text: "Your reward is held safely until you approve." },
                { icon: Globe, title: "Real humans, worldwide", text: "Hunters complete tasks wherever they're needed." },
                { icon: Clock, title: "Deadlines you set", text: "Listings run 7 days on Free, up to 30 on Pro." },
              ].map((s) => (
                <div key={s.title} className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-sm">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-[hsl(45_95%_55%)]/15 text-[hsl(38_80%_40%)] dark:text-[hsl(45_95%_60%)]">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-sm font-bold">{s.title}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{s.text}</div>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
        </section>

        {/* Task categories */}
        <section className="container pb-14 md:pb-20">
          <FadeIn inView>
            <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
              <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight mb-3">What can you get done?</h2>
              <p className="text-sm md:text-base text-muted-foreground">
                Anything that needs human eyes, ears, or legs — set the proof you want, fund a reward, and a hunter delivers it.
              </p>
            </div>
          </FadeIn>
          <StaggerContainer inView className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { slug: "local-photos", icon: Camera, title: "Local photos", examples: ["A storefront or landmark photo", "Condition of a listed item", "A street at a specific time"] },
              { slug: "website-checks", icon: Globe, title: "Website checks", examples: ["Is this site live right now?", "Test a form or checkout", "Screenshot behind a login"] },
              { slug: "real-world-answers", icon: MessageCircleQuestion, title: "Real-world answers", examples: ["Opening hours and prices", "Is this in stock locally?", "A question only locals know"] },
              { slug: "data-research", icon: Search, title: "Data & research", examples: ["Compile a list of contacts", "Find a hard-to-locate fact", "Verify numbers and details"] },
              { slug: "audio-video", icon: Video, title: "Audio & video", examples: ["A short walkthrough video", "Record a voicemail or call", "Ambient audio of a place"] },
              { slug: "verifications", icon: ShieldCheck, title: "Verifications", examples: ["Address or identity check", "Receipt and document validation", "Signage at a business"] },
            ].map((c) => (
              <StaggerItem key={c.title} className="h-full">
                <Link
                  to={`/tasks/${c.slug}`}
                  className="group flex h-full flex-col rounded-2xl border border-border bg-card p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-[hsl(45_90%_50%)]/50 hover:shadow-lg"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[hsl(45_95%_55%)]/15 text-[hsl(38_80%_40%)] dark:text-[hsl(45_95%_60%)] mb-4">
                    <c.icon className="h-5 w-5" />
                  </span>
                  <div className="font-bold mb-2">{c.title}</div>
                  <ul className="space-y-1.5 text-sm text-muted-foreground mb-4">
                    {c.examples.map((e) => (
                      <li key={e} className="flex items-start gap-2">
                        <CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(38_80%_42%)] mt-0.5" />
                        <span>{e}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-auto flex items-center justify-between text-sm pt-3 border-t border-border">
                    <span className="text-muted-foreground">Rewards from {formatCurrency(BOUNTY_CONFIG.FREE_MIN_REWARD_CENTS)}</span>
                    <span className="inline-flex items-center gap-1 font-semibold text-primary">
                      Learn more <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </span>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </section>

        {/* How it works */}
        <section className="container pb-14 md:pb-20">
          <FadeIn inView>
            <div className="text-center max-w-2xl mx-auto mb-8 md:mb-10">
              <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight mb-3">How it works</h2>
              <p className="text-sm md:text-base text-muted-foreground">Three steps between you and a finished task.</p>
            </div>
          </FadeIn>
          <StaggerContainer inView className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            {[
              { icon: Plus, title: "Describe", text: "Set the task and proof needed" },
              { icon: DollarSign, title: "Fund", text: "Choose and fund the reward" },
              { icon: Camera, title: "Review", text: "Approve proof before payout" },
            ].map((s, i) => (
              <StaggerItem key={s.title} className="h-full">
                <div className="relative h-full rounded-2xl border border-border bg-card p-6 text-center shadow-sm">
                  <span className="pointer-events-none absolute -right-1 -top-3 text-6xl font-extrabold text-[hsl(45_90%_50%)]/15 select-none">
                    {i + 1}
                  </span>
                  <span className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)] shadow-sm mb-4">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <div className="font-bold mb-1">{s.title}</div>
                  <div className="text-sm text-muted-foreground">{s.text}</div>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </section>

        {/* Feed */}
        <section id="feed" className="container pb-20 scroll-mt-24">
          <FadeIn inView>
          <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
            <div>
              <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight">The Bounty Board</h2>
              <p className="text-sm text-muted-foreground flex items-center gap-2">
                Browse posted tasks and their proof requirements. Availability varies.
                {isLive && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-600 text-white text-xs rounded-full animate-pulse">
                    <Wifi className="h-3 w-3" />
                    Updated
                  </span>
                )}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              {/* Country Filter */}
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger aria-label="Filter tasks by country" className="w-[180px] h-9 text-sm rounded-full">
                  <SelectValue placeholder="All countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All countries</SelectItem>
                  {useMemo(() => {
                    const countries = Array.from(new Set(
                      bounties
                        .map(b => b.country)
                        .filter(Boolean)
                    )).sort() as string[];
                    return countries.map(code => (
                      <SelectItem key={code} value={code}>
                        {countryToFlag(code)} {COUNTRY_NAMES[code] || code}
                      </SelectItem>
                    ));
                  }, [bounties])}
                </SelectContent>
              </Select>
              <Tabs value={filter} onValueChange={(v) => setFilter(v as any)}>
                <TabsList className="rounded-full">
                  <TabsTrigger value="open">Open</TabsTrigger>
                  <TabsTrigger value="all">All</TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
          </div>
          </FadeIn>

          {/* Spotlight — featured bounties */}
          {!loading && !loadFailed && featuredBounties.length > 0 && (
            <FadeIn inView className="mb-8">
              <div className="grid md:grid-cols-2 gap-5">
                {featuredBounties.map((b) => (
                  <motion.div
                    key={b.id}
                    whileHover={{ y: -6 }}
                    transition={{ type: "spring", stiffness: 260, damping: 24 }}
                    className="h-full"
                  >
                    <Link
                      to={ROUTES.BOUNTY_DETAIL(b.id)}
                      className="poster poster-featured block h-full p-6 rounded-2xl text-left transition-colors hover:border-[hsl(45_90%_50%)]/70"
                    >
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <span className="reward-badge text-[10px] uppercase tracking-widest">
                          <Star className="h-3 w-3" fill="currentColor" /> Featured
                        </span>
                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {formatTimeRemaining(b.expires_at)} left
                        </span>
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold leading-snug mb-3 line-clamp-2">{b.title}</h3>
                      <div className="flex flex-wrap items-center gap-2 mb-4 text-xs">
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-secondary text-secondary-foreground rounded-full capitalize">
                          <Camera className="h-3 w-3" /> {b.proof_type} proof
                        </span>
                        {b.country && (
                          <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-secondary text-secondary-foreground rounded-full">
                            {countryToFlag(b.country)} {COUNTRY_NAMES[b.country] || b.country}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-3">
                        <span className="reward-badge text-base sm:text-lg">{formatCurrency(b.reward_cents)}</span>
                        <span className="inline-flex items-center gap-1 text-sm font-semibold text-primary">
                          View &amp; claim <ArrowRight className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>
            </FadeIn>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-5 shadow-sm space-y-3">
                  <div className="flex items-center justify-between">
                    <Skeleton className="h-4 w-16 rounded" />
                    <Skeleton className="h-3 w-20 rounded" />
                  </div>
                  <Skeleton className="h-5 w-3/4 rounded" />
                  <Skeleton className="h-4 w-1/2 rounded" />
                  <Skeleton className="h-16 w-full rounded" />
                  <div className="flex items-center justify-between pt-1">
                    <Skeleton className="h-7 w-20 rounded" />
                    <Skeleton className="h-8 w-24 rounded" />
                  </div>
                </div>
              ))}
            </div>
          ) : (() => {
            const filtered = countryFilter === "all"
              ? bounties
              : bounties.filter(b => b.country === countryFilter);
            return filtered.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-border bg-card/60 p-6 sm:p-14 text-center">
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-[hsl(45_95%_55%)]/15 border border-[hsl(45_90%_50%)]/30">
                  {loadFailed
                    ? <Wifi className="h-6 w-6 text-muted-foreground" />
                    : <Target className="h-6 w-6 text-[hsl(38_80%_42%)]" />}
                </div>
                <h3 className="text-xl font-bold mb-2">
                  {loadFailed ? 'Tasks could not be loaded' : 'The board is quiet right now'}
                </h3>
                <p className="text-muted-foreground mb-5 max-w-md mx-auto">
                  {loadFailed
                    ? 'Please try again later.'
                    : countryFilter !== "all"
                      ? `No matching tasks in ${COUNTRY_NAMES[countryFilter] || countryFilter}. Try a different filter.`
                      : 'Be the first to post a task — set the reward and proof you want, and hunters anywhere can claim it.'}
                </p>
                {countryFilter !== "all" ? (
                  <Button variant="outline" onClick={() => setCountryFilter("all")}>Clear Country Filter</Button>
                ) : (
                  <div className="flex flex-wrap items-center justify-center gap-3">
                    <Button asChild className="shine-sweep bg-gradient-to-r from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)] font-bold border-none">
                      <Link to={ROUTES.POST_BOUNTY} onClick={() => trackFunnelEvent('entry_intent', { intent: 'post', source: 'landing_empty' })}>Post a task</Link>
                    </Button>
                    <Button asChild variant="outline">
                      <Link to="/wall" onClick={() => trackFunnelEvent('entry_intent', { intent: 'earn', source: 'landing_empty' })}>See the Bounty Wall</Link>
                    </Button>
                  </div>
                )}
              </div>
            ) : (
              <StaggerContainer inView className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {filtered.map((b) => (
                  <StaggerItem key={b.id} className="h-full">
                    <BountyCard bounty={b} />
                  </StaggerItem>
                ))}
              </StaggerContainer>
            );
          })()}
        </section>

        {/* Pricing */}
        <section id="pricing" className="container pb-20 scroll-mt-24">
          <FadeIn inView className="max-w-3xl mx-auto">
            <div className="text-center max-w-xl mx-auto mb-8">
              <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight mb-3">How funding works</h2>
              <p className="text-sm md:text-base text-muted-foreground">You fund the reward plus a service fee. Hunters keep 100% of the reward.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="rounded-2xl border border-border bg-card p-6 shadow-sm">
                <div className="font-bold text-lg mb-3">Free</div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(38_80%_42%)] mt-0.5" /> Rewards {formatCurrency(BOUNTY_CONFIG.FREE_MIN_REWARD_CENTS)}–{formatCurrency(BOUNTY_CONFIG.FREE_MAX_REWARD_CENTS)} per hunter</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(38_80%_42%)] mt-0.5" /> 7-day listings · 1–{BOUNTY_CONFIG.MAX_HUNTERS} hunters</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(38_80%_42%)] mt-0.5" /> {Math.round(BOUNTY_CONFIG.PLATFORM_FEE_PERCENTAGE * 100)}% service fee, min {formatCurrency(BOUNTY_CONFIG.MIN_PLATFORM_FEE_CENTS)}</li>
                </ul>
                <Button asChild variant="outline" className="w-full mt-5">
                  <Link to={ROUTES.POST_BOUNTY} onClick={() => trackFunnelEvent('entry_intent', { intent: 'post', source: 'pricing_free' })}>Start posting free</Link>
                </Button>
              </div>
              <div className="relative rounded-2xl border-2 border-[hsl(45_90%_50%)] bg-card p-6 shadow-lg">
                <span className="absolute -top-3 left-6 reward-badge text-[10px] uppercase tracking-widest">Best for power users</span>
                <div className="font-bold text-lg mb-3 flex items-center gap-2">
                  Pro
                </div>
                <ul className="space-y-2 text-sm text-muted-foreground">
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(38_80%_42%)] mt-0.5" /> Rewards {formatCurrency(BOUNTY_CONFIG.PRO_MIN_REWARD_CENTS)}–{formatCurrency(BOUNTY_CONFIG.PRO_MAX_REWARD_CENTS)} per hunter · up to 30-day listings</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(38_80%_42%)] mt-0.5" /> {Math.round(BOUNTY_CONFIG.PRO_PLATFORM_FEE_PERCENTAGE * 100)}% service fee, min {formatCurrency(BOUNTY_CONFIG.PRO_MIN_PLATFORM_FEE_CENTS)}</li>
                  <li className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 shrink-0 text-[hsl(38_80%_42%)] mt-0.5" /> $9.99 / 30 days or $79.99 / 365 days · no auto-renewal</li>
                </ul>
                <Button asChild className="w-full mt-5 bg-gradient-to-r from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)] font-bold border-none hover:from-[hsl(45_90%_50%)] hover:to-[hsl(35_84%_42%)]">
                  <Link to={ROUTES.SUBSCRIPTION} onClick={() => trackFunnelEvent('entry_intent', { intent: 'pro', source: 'pricing_pro' })}>Go Pro</Link>
                </Button>
              </div>
            </div>
            <details className="mt-5 text-sm max-w-2xl mx-auto text-muted-foreground">
              <summary className="cursor-pointer font-medium text-center">Full funding details</summary>
              <p className="mt-2">
                Free: {formatCurrency(BOUNTY_CONFIG.FREE_MIN_REWARD_CENTS)}–{formatCurrency(BOUNTY_CONFIG.FREE_MAX_REWARD_CENTS)} per hunter, 7-day default and maximum. Pro: {formatCurrency(BOUNTY_CONFIG.PRO_MIN_REWARD_CENTS)}–{formatCurrency(BOUNTY_CONFIG.PRO_MAX_REWARD_CENTS)}, 7-day default, up to 30 days. Rewards and service fees are funded by you.
              </p>
            </details>
            {INTRO_OFFER_ENABLED && (
              <div className="mt-5 mx-auto max-w-2xl text-sm text-center">
                <p className="font-semibold">New launch accounts: {INTRO_OFFER_CONFIG.credits} service-fee credits worth up to {formatCurrency(INTRO_OFFER_CONFIG.maxDiscountCents)} each after email confirmation.</p>
                <p className="mt-1 text-muted-foreground">Redeem within {INTRO_OFFER_CONFIG.redemptionDays} days. Each credited listing runs for {INTRO_OFFER_CONFIG.listingDurationHours / 24} days. Rewards, remaining fees, and extras still apply.</p>
                <details className="mt-2 text-muted-foreground">
                  <summary className="cursor-pointer font-medium">Introductory offer terms</summary>
                  <p className="mt-2">{INTRO_OFFER_TERMS}</p>
                </details>
              </div>
            )}
          </FadeIn>
        </section>

        {/* Final CTA band */}
        <section className="container pb-20">
          <FadeIn inView>
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[hsl(45_95%_55%)] via-[hsl(40_92%_50%)] to-[hsl(32_85%_44%)] px-6 py-12 md:px-12 md:py-16 text-center shadow-xl">
              <div aria-hidden className="absolute inset-0 bg-[radial-gradient(closest-side,hsl(0_0%_100%/0.35),transparent)] blur-md" />
              <div className="relative">
                <h2 className="text-2xl md:text-4xl font-extrabold tracking-tight text-[hsl(24_45%_12%)] mb-3">
                  Have a task that needs a human?
                </h2>
                <p className="text-[hsl(24_40%_20%)] md:text-lg max-w-xl mx-auto mb-7">
                  Draft it free in under a minute. Fund the reward only when you're ready to publish — and pay out only after you approve the proof.
                </p>
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
                  <Button asChild size="lg" className="px-8 py-3 text-base sm:text-lg bg-[hsl(24_45%_12%)] text-white hover:bg-[hsl(24_45%_16%)] shadow-lg border-none">
                    <Link to={ROUTES.POST_BOUNTY} onClick={() => trackFunnelEvent('entry_intent', { intent: 'post', source: 'cta_band' })}><Plus className="h-5 w-5 mr-2" /> Post your first task</Link>
                  </Button>
                </motion.div>
                <p className="mt-4 text-sm text-[hsl(24_40%_20%)]">
                  Or <a href="#feed" className="font-bold underline underline-offset-4 hover:opacity-80">browse open tasks to earn</a>
                </p>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* Footer - Only visible when NOT logged in */}
        {!user && (
          <footer className="container mt-4 pt-8 border-t border-border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
              {/* Brand */}
              <div className="md:col-span-1">
                <div className="flex items-center gap-2 mb-2">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-[hsl(45_95%_55%)] to-[hsl(35_88%_46%)] text-[hsl(24_45%_12%)]">
                    <Target className="h-4 w-4" />
                  </span>
                  <h3 className="font-extrabold text-lg tracking-tight">BountyClaimer</h3>
                </div>
                <p className="text-sm text-muted-foreground">
                  Get human tasks done with proof
                </p>
              </div>
              
              {/* Product */}
              <div>
                <h4 className="font-semibold text-sm mb-3">Product</h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to={ROUTES.POST_BOUNTY} className="text-muted-foreground hover:text-foreground transition-colors">Post a task</Link></li>
                  <li><a href="/#feed" className="text-muted-foreground hover:text-foreground transition-colors">Earn rewards</a></li>
                  <li><Link to="/tasks/local-photos" className="text-muted-foreground hover:text-foreground transition-colors">Local photos</Link></li>
                  <li><Link to="/tasks/website-checks" className="text-muted-foreground hover:text-foreground transition-colors">Website checks</Link></li>
                  <li><Link to="/tasks/real-world-answers" className="text-muted-foreground hover:text-foreground transition-colors">Real-world answers</Link></li>
                  <li><Link to="/wall" className="text-muted-foreground hover:text-foreground transition-colors">Bounty Wall</Link></li>
                  <li><Link to="/founders-wall" className="text-muted-foreground hover:text-foreground transition-colors">Backers</Link></li>
                  <li><Link to={ROUTES.SUBSCRIPTION} className="text-muted-foreground hover:text-foreground transition-colors">Pro subscription</Link></li>
                </ul>
              </div>
              
              {/* Legal Links */}
              <div>
                <h4 className="font-semibold text-sm mb-3">Legal</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <button 
                      onClick={() => setShowPrivacyPolicy(true)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Privacy Policy
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setShowTermsOfService(true)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Terms of Service
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setShowCookiePolicy(true)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Cookie Policy
                    </button>
                  </li>
                </ul>
              </div>
              
              {/* GDPR Compliance */}
              <div>
                <h4 className="font-semibold text-sm mb-3">GDPR</h4>
                <ul className="space-y-2 text-sm">
                  <li>
                    <button 
                      onClick={() => setShowDataProcessing(true)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Data Processing
                    </button>
                  </li>
                  <li>
                    <button 
                      onClick={() => setShowDataRights(true)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      Your Rights
                    </button>
                  </li>
                </ul>
              </div>
            </div>
            
            <div className="pt-6 border-t border-border text-center text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-between gap-3">
              <p>© {new Date().getFullYear()} BountyClaimer. All rights reserved.</p>
              <p>Compliant with GDPR, CCPA, and international data protection regulations.</p>
              {/* Hidden Admin Access */}
              <button
                onClick={() => setShowAdminAccess(true)}
                className="text-[10px] text-muted-foreground/30 hover:text-muted-foreground/70 transition-all"
                title="System Status"
              >
                System
              </button>
            </div>
          </footer>
        )}
      </main>

      {/* Admin Secure Access Dialog */}
      <Dialog open={showAdminAccess} onOpenChange={setShowAdminAccess}>
        <DialogContent className="max-w-md">
          <AdminSecureAccess
            onSuccess={() => setShowAdminAccess(false)}
            onCancel={() => setShowAdminAccess(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Privacy Policy Dialog */}
      <Dialog open={showPrivacyPolicy} onOpenChange={setShowPrivacyPolicy}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">Privacy Policy</h2>
              <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()} | Effective immediately</p>
            </div>
            
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div className="bg-muted/50 p-4 rounded-lg border-l-4 border-primary">
                <p className="text-sm font-medium">BountyClaimer is committed to protecting your privacy. We collect minimal data necessary to operate our human-powered task platform.</p>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">1. Information We Collect</h3>
              <div className="space-y-2 text-sm">
                <h4 className="font-medium">Account Information:</h4>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Email address (for authentication and notifications)</li>
                  <li>Display name (public, visible on bounties you post or claim)</li>
                  <li>Profile information (optional, for community features)</li>
                </ul>
                
                <h4 className="font-medium mt-4">Platform Activity:</h4>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Bounty postings (title, description, reward amount, deadline)</li>
                  <li>Claim submissions (proof of completion, completion timestamps)</li>
                  <li>Wallet transactions (deposits, withdrawals, refunds via Stripe)</li>
                  <li>Subscription status (Free tier or Pro membership)</li>
                </ul>
                
                <h4 className="font-medium mt-4">Technical Data:</h4>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Device information and browser type</li>
                  <li>IP address (for security and fraud prevention)</li>
                  <li>Usage analytics via Vercel Analytics (anonymized)</li>
                </ul>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. How We Use Your Data</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Platform Operation:</strong> Process bounty postings, claims, and payments</li>
                <li><strong>Security:</strong> Detect fraud, enforce platform rules, prevent abuse via Armadillo Security framework</li>
                <li><strong>Notifications:</strong> Alert you about bounty status changes, claims, and refunds</li>
                <li><strong>Improvements:</strong> Analyze usage patterns to enhance features</li>
                <li><strong>Compliance:</strong> Meet legal obligations (tax records, dispute resolution)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Data Sharing & Third Parties</h3>
              <p className="text-sm text-muted-foreground mb-3">We <strong>never sell</strong> your personal data. We share only what's necessary with:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Stripe:</strong> Payment processing (wallet deposits, withdrawals, Pro subscriptions)</li>
                <li><strong>Supabase:</strong> Database hosting and authentication (encrypted at rest)</li>
                <li><strong>Vercel:</strong> Platform hosting and analytics (anonymized usage data)</li>
                <li><strong>Law Enforcement:</strong> Only when legally required (court order, subpoena)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Data Security</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Encryption:</strong> All data encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                <li><strong>Authentication:</strong> Supabase Auth with email verification and optional 2FA</li>
                <li><strong>Access Controls:</strong> Role-based permissions (users can only access their own data)</li>
                <li><strong>Row-Level Security:</strong> Database-level isolation prevents unauthorized data access</li>
                <li><strong>Armadillo Security:</strong> Real-time threat detection and trust scoring</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Data Retention</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Active Accounts:</strong> Data retained while your account exists</li>
                <li><strong>Deleted Accounts:</strong> Personal data removed within 30 days; anonymized transaction records kept for 7 years (legal compliance)</li>
                <li><strong>Bounty Data:</strong> Deleted bounties removed immediately; completed bounties kept for dispute resolution (90 days)</li>
                <li><strong>You Can Request Deletion:</strong> Email claimerbounty@gmail.com anytime</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Your Privacy Rights</h3>
              <p className="text-sm text-muted-foreground mb-3">Under GDPR, CCPA, and international law, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>Access all data we hold about you</li>
                <li>Correct inaccurate information</li>
                <li>Request complete deletion ("right to be forgotten")</li>
                <li>Export your data in machine-readable format</li>
                <li>Object to processing or restrict usage</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">Contact: <strong>claimerbounty@gmail.com</strong> | Response time: 30 days maximum</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">7. Children's Privacy</h3>
              <p className="text-sm text-muted-foreground">BountyClaimer is not intended for users under 18. We do not knowingly collect data from minors. If we discover underage usage, the account will be terminated and data deleted.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">8. Changes to This Policy</h3>
              <p className="text-sm text-muted-foreground">We may update this policy. Material changes will be communicated via email or platform notification. Continued use after changes constitutes acceptance.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Terms of Service Dialog */}
      <Dialog open={showTermsOfService} onOpenChange={setShowTermsOfService}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">Terms of Service</h2>
              <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()} | By using BountyClaimer, you agree to these terms</p>
            </div>
            
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div className="bg-muted/50 p-4 rounded-lg border-l-4 border-primary">
                <p className="text-sm font-medium">BountyClaimer is a human-powered task marketplace. These terms govern your use of our platform.</p>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">1. Platform Overview</h3>
              <p className="text-sm text-muted-foreground">BountyClaimer connects task requesters with human workers who complete bounties that AI cannot handle. Users can post bounties with rewards, claim tasks, submit proof, and earn money.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Account Requirements</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>You must be 18+ years old to use the platform</li>
                <li>One account per person (no duplicate accounts)</li>
                <li>Accurate email required (verified via Supabase Auth)</li>
                <li>You are responsible for account security (enable 2FA recommended)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Bounty Posting Rules</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Rewards per hunter:</strong> $2–$10,000 on Free; $0.50–$10,000 on Pro. Fund 1–10 hunters per listing.</li>
                <li><strong>Duration:</strong> Seven days by default on both plans. Ordinary listings have a seven-day maximum on Free and a 30-day maximum on Pro.</li>
                <li><strong>Service Fee:</strong> Free: 20%, minimum $0.40 per listing. Pro: 10%, minimum $0.20. You fund rewards plus service fees, applicable processing charges, and selected extras.</li>
                {INTRO_OFFER_ENABLED && <li><strong>Introductory offer:</strong> {INTRO_OFFER_CONFIG.credits} service-fee credits worth up to $1 each. {INTRO_OFFER_TERMS}</li>}
                <li><strong>No Prohibited Content:</strong> Illegal tasks, harassment, spam, or fraudulent bounties will be deleted</li>
                <li><strong>Admin Moderation:</strong> We reserve the right to delete inappropriate bounties with automatic refund</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Bounty Claiming & Completion</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>First-Come-First-Served:</strong> Fair claiming system prevents hoarding</li>
                <li><strong>Proof Required:</strong> Screenshots, descriptions, or files proving completion</li>
                <li><strong>Requester Approval:</strong> Bounty owner must approve submission for payment</li>
                <li><strong>Dispute Resolution:</strong> Contact claimerbounty@gmail.com if issues arise</li>
                <li><strong>No Fraud:</strong> Fake proof or manipulation results in account ban</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Payments & Wallet</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Stripe Integration:</strong> All payments processed securely via Stripe</li>
                <li><strong>Deposits:</strong> Add funds to wallet via credit/debit card</li>
                <li><strong>Withdrawals:</strong> Request payout (manual processing, min/max limits apply)</li>
                <li><strong>Refunds:</strong> Admin can refund credits for deleted bounties or service issues</li>
                <li><strong>No Chargebacks:</strong> Fraudulent disputes may result in account suspension</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Pro Subscription</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Benefits:</strong> Lower service fees, rewards from $0.50 to $10,000 per hunter, analytics dashboard, priority support, and up to 30-day listings. Bulk batches are limited to 10 listings.</li>
                <li><strong>Billing:</strong> $9.99 for 30 days or $79.99 for 365 days, paid via Stripe. Purchases do not automatically renew.</li>
                <li><strong>Refunds:</strong> Prorated refunds available within 7 days of purchase</li>
                <li><strong>Downgrade:</strong> Reverts to Free tier at end of billing cycle</li>
                <li><strong>Admin-Granted:</strong> Admin can manually award/revoke Pro status</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">7. Prohibited Activities</h3>
              <p className="text-sm text-muted-foreground mb-3">You may NOT:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>Submit false or misleading proof of completion</li>
                <li>Create multiple accounts to manipulate the system</li>
                <li>Post illegal, harmful, or fraudulent bounties</li>
                <li>Harass, threaten, or discriminate against other users</li>
                <li>Use bots, scripts, or automation to claim bounties</li>
                <li>Attempt to bypass security measures (Armadillo framework)</li>
                <li>Exploit bugs or vulnerabilities (report them instead for rewards!)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">8. Account Termination & Moderation</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Admin Powers:</strong> We can ban, suspend, or restrict accounts for violations</li>
                <li><strong>Temporary Suspension:</strong> Timed restrictions for minor infractions</li>
                <li><strong>Permanent Ban:</strong> Severe or repeated violations result in permanent removal</li>
                <li><strong>Wallet Forfeiture:</strong> Banned accounts may lose remaining balance (fraud cases)</li>
                <li><strong>Appeals:</strong> Email claimerbounty@gmail.com to dispute actions</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">9. Intellectual Property</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>You retain ownership of content you create</li>
                <li>By posting bounties, you grant platform license to display them</li>
                <li>Platform code, design, and branding are proprietary (no copying)</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">10. Limitation of Liability</h3>
              <p className="text-sm text-muted-foreground">BountyClaimer is provided "AS IS" without warranties. We are not liable for: lost profits, data loss, unauthorized access, or disputes between users. Maximum liability capped at amounts you paid in the last 12 months.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">11. Dispute Resolution</h3>
              <p className="text-sm text-muted-foreground">Disputes resolved via good-faith negotiation first. If unresolved, binding arbitration under [Your Jurisdiction] law. Class action waivers apply.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">12. Contact</h3>
              <p className="text-sm text-muted-foreground">Questions? Email: <strong>claimerbounty@gmail.com</strong></p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cookie Policy Dialog */}
      <Dialog open={showCookiePolicy} onOpenChange={setShowCookiePolicy}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">Cookie Policy</h2>
              <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()} | How we use cookies and tracking</p>
            </div>
            
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div className="bg-muted/50 p-4 rounded-lg border-l-4 border-primary">
                <p className="text-sm font-medium">BountyClaimer uses minimal cookies essential for platform operation. We respect your privacy and comply with GDPR/ePrivacy regulations.</p>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">1. What Are Cookies?</h3>
              <p className="text-sm text-muted-foreground">Cookies are small text files stored on your device when you visit websites. They help us remember your preferences, keep you logged in, and understand how you use the platform.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Cookies We Use</h3>
              <div className="space-y-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-base mb-2">Essential Cookies (Required)</h4>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                    <li><strong>Authentication:</strong> Supabase Auth session cookies keep you logged in securely</li>
                    <li><strong>Security:</strong> CSRF tokens protect against cross-site request forgery attacks</li>
                    <li><strong>Preferences:</strong> Theme settings (light/dark mode) stored locally</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2"><em>Cannot be disabled - required for platform functionality</em></p>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-base mb-2">Analytics Cookies (Optional)</h4>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                    <li><strong>Vercel Analytics:</strong> Anonymous usage data (pages visited, time on site, device type)</li>
                    <li><strong>Performance:</strong> Page load times and error tracking</li>
                  </ul>
                  <p className="text-xs text-muted-foreground mt-2"><em>No personal identifiers collected. Data is aggregated and anonymized.</em></p>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-base mb-2">Third-Party Cookies</h4>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                    <li><strong>Stripe:</strong> Payment processing cookies during checkout (PCI compliant)</li>
                    <li><strong>Supabase:</strong> Database session management (encrypted)</li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. How We Use Cookie Data</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Authentication:</strong> Maintain secure login sessions</li>
                <li><strong>Security:</strong> Detect suspicious activity and prevent fraud</li>
                <li><strong>Improvements:</strong> Understand which features are most used</li>
                <li><strong>Performance:</strong> Identify and fix slow-loading pages</li>
                <li><strong>Compliance:</strong> Meet legal requirements for data transparency</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Managing Cookies</h3>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p><strong>Browser Settings:</strong> You can control cookies through your browser:</p>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Chrome: Settings → Privacy and security → Cookies</li>
                  <li>Firefox: Settings → Privacy & Security → Cookies</li>
                  <li>Safari: Preferences → Privacy → Block cookies</li>
                  <li>Edge: Settings → Cookies and site permissions</li>
                </ul>
                <p className="mt-3"><strong>Warning:</strong> Blocking essential cookies will prevent you from logging in or using the platform.</p>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Cookie Duration</h3>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Cookie Type</th>
                    <th className="text-left py-2">Duration</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b">
                    <td className="py-2">Auth Session</td>
                    <td className="py-2">Until logout or 1 week</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Theme Preference</td>
                    <td className="py-2">1 year (localStorage)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Analytics</td>
                    <td className="py-2">13 months (Vercel)</td>
                  </tr>
                  <tr>
                    <td className="py-2">Stripe Payment</td>
                    <td className="py-2">Session only (deleted after checkout)</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Your Consent</h3>
              <p className="text-sm text-muted-foreground">By using BountyClaimer, you consent to our use of cookies as described in this policy. Essential cookies are processed under "legitimate interest" (GDPR Art. 6(1)(f)). Analytics cookies require your implicit consent through continued use.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">7. Updates</h3>
              <p className="text-sm text-muted-foreground">We may update this policy as we add new features or comply with new regulations. Changes will be posted here with updated date.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">8. Contact</h3>
              <p className="text-sm text-muted-foreground">Questions about cookies? Email: <strong>claimerbounty@gmail.com</strong></p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Data Processing Dialog */}
      <Dialog open={showDataProcessing} onOpenChange={setShowDataProcessing}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">Data Processing Agreement</h2>
              <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()} | GDPR Article 28 Compliance</p>
            </div>
            
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div className="bg-muted/50 p-4 rounded-lg border-l-4 border-primary">
                <p className="text-sm font-medium">This agreement outlines how BountyClaimer processes personal data on behalf of users, in compliance with GDPR and international data protection laws.</p>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">1. Data Controller & Processor</h3>
              <div className="space-y-3 text-sm text-muted-foreground">
                <p><strong>Data Controller:</strong> BountyClaimer (claimerbounty@gmail.com)</p>
                <p><strong>Data Processor:</strong> Third-party service providers (listed below)</p>
                <p><strong>Data Subject:</strong> You (the user)</p>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Legal Basis for Processing</h3>
              <p className="text-sm text-muted-foreground mb-3">We process your data under the following GDPR Article 6 legal bases:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Contract Performance (Art. 6(1)(b)):</strong> Processing necessary to operate the platform (posting bounties, claiming tasks, processing payments)</li>
                <li><strong>Legitimate Interest (Art. 6(1)(f)):</strong> Fraud prevention, security monitoring (Armadillo framework), platform improvements</li>
                <li><strong>Consent (Art. 6(1)(a)):</strong> Analytics cookies, marketing communications (you can withdraw anytime)</li>
                <li><strong>Legal Obligation (Art. 6(1)(c)):</strong> Tax records, law enforcement compliance</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Data Processors We Use</h3>
              <div className="space-y-3">
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-base mb-2">Supabase (Database & Auth)</h4>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                    <li><strong>Service:</strong> PostgreSQL database hosting, user authentication</li>
                    <li><strong>Data:</strong> Account info, bounties, claims, wallet balances</li>
                    <li><strong>Location:</strong> [Your Region - check Supabase dashboard]</li>
                    <li><strong>Security:</strong> AES-256 encryption at rest, TLS 1.3 in transit</li>
                    <li><strong>DPA:</strong> <a href="https://supabase.com/privacy" target="_blank" className="text-primary hover:underline">Supabase Privacy Policy</a></li>
                  </ul>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-base mb-2">Stripe (Payments)</h4>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                    <li><strong>Service:</strong> Payment processing for deposits, withdrawals, Pro subscriptions</li>
                    <li><strong>Data:</strong> Payment methods, transaction history, billing info</li>
                    <li><strong>Compliance:</strong> PCI DSS Level 1 (highest security standard)</li>
                    <li><strong>DPA:</strong> <a href="https://stripe.com/privacy" target="_blank" className="text-primary hover:underline">Stripe Privacy Policy</a></li>
                  </ul>
                </div>

                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-base mb-2">Vercel (Hosting & Analytics)</h4>
                  <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                    <li><strong>Service:</strong> Frontend hosting, CDN, usage analytics</li>
                    <li><strong>Data:</strong> Anonymized page views, device info, IP addresses (hashed)</li>
                    <li><strong>Retention:</strong> 13 months for analytics data</li>
                    <li><strong>DPA:</strong> <a href="https://vercel.com/legal/privacy-policy" target="_blank" className="text-primary hover:underline">Vercel Privacy Policy</a></li>
                  </ul>
                </div>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. International Data Transfers</h3>
              <p className="text-sm text-muted-foreground mb-3">Your data may be transferred outside the European Economic Area (EEA). We ensure adequate protection through:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Standard Contractual Clauses (SCCs):</strong> EU-approved contractual safeguards</li>
                <li><strong>Adequacy Decisions:</strong> Transfers only to countries with EU-recognized data protection</li>
                <li><strong>Binding Corporate Rules:</strong> Internal safeguards for multinational processors</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Data Security Measures</h3>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Encryption:</strong> All data encrypted in transit (TLS 1.3) and at rest (AES-256)</li>
                <li><strong>Access Control:</strong> Role-based permissions, Row-Level Security (RLS) in database</li>
                <li><strong>Authentication:</strong> Supabase Auth with email verification, optional 2FA</li>
                <li><strong>Monitoring:</strong> Armadillo Security framework detects threats in real-time</li>
                <li><strong>Backups:</strong> Automated daily backups with 30-day retention</li>
                <li><strong>Audit Logs:</strong> All admin actions logged in moderation_actions table</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Data Retention Periods</h3>
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2">Data Type</th>
                    <th className="text-left py-2">Retention Period</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-b">
                    <td className="py-2">Account Data</td>
                    <td className="py-2">Until account deletion + 30 days</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Transaction Records</td>
                    <td className="py-2">7 years (tax compliance)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Bounty Data</td>
                    <td className="py-2">90 days after completion (dispute resolution)</td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-2">Analytics Data</td>
                    <td className="py-2">13 months (Vercel policy)</td>
                  </tr>
                  <tr>
                    <td className="py-2">Moderation Logs</td>
                    <td className="py-2">2 years (audit trail)</td>
                  </tr>
                </tbody>
              </table>

              <h3 className="text-xl font-semibold mt-6 mb-3">7. Your Rights</h3>
              <p className="text-sm text-muted-foreground mb-3">As a data subject, you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>Access all personal data we hold about you</li>
                <li>Rectify inaccurate or incomplete information</li>
                <li>Erase your data ("right to be forgotten")</li>
                <li>Restrict or object to processing</li>
                <li>Data portability (export in machine-readable format)</li>
                <li>Withdraw consent at any time</li>
                <li>Lodge complaint with supervisory authority</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">Exercise these rights: <strong>claimerbounty@gmail.com</strong> | Response time: 30 days</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">8. Data Breach Notification</h3>
              <p className="text-sm text-muted-foreground">In the event of a data breach, we will notify affected users within 72 hours and report to supervisory authorities as required by GDPR Article 33.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">9. Contact</h3>
              <p className="text-sm text-muted-foreground">Data Protection Officer: <strong>claimerbounty@gmail.com</strong></p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Your Rights Dialog */}
      <Dialog open={showDataRights} onOpenChange={setShowDataRights}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <div className="space-y-6">
            <div>
              <h2 className="text-3xl font-bold mb-2">Your Data Rights (GDPR)</h2>
              <p className="text-sm text-muted-foreground">Last updated: {new Date().toLocaleDateString()} | Your rights under EU General Data Protection Regulation</p>
            </div>
            
            <div className="prose prose-sm dark:prose-invert max-w-none">
              <div className="bg-muted/50 p-4 rounded-lg border-l-4 border-primary">
                <p className="text-sm font-medium">Under GDPR, you have extensive rights over your personal data. BountyClaimer is committed to honoring these rights promptly.</p>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">1. Right to Access (Article 15)</h3>
              <p className="text-sm text-muted-foreground mb-3">You can request a complete copy of all personal data we hold about you, including:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>Account information (email, display name, profile data)</li>
                <li>All bounties you've posted or claimed</li>
                <li>Wallet transaction history</li>
                <li>Subscription status and payment records</li>
                <li>Moderation actions taken on your account</li>
                <li>Login history and device information</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3"><strong>How to exercise:</strong> Email claimerbounty@gmail.com with "Data Access Request" | We respond within 30 days</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">2. Right to Rectification (Article 16)</h3>
              <p className="text-sm text-muted-foreground mb-3">You can request correction of inaccurate or incomplete data:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>Update your email address or display name in Profile Settings</li>
                <li>Correct outdated information by contacting us</li>
                <li>We will verify the correction and update within 14 days</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">3. Right to Erasure / "Right to Be Forgotten" (Article 17)</h3>
              <p className="text-sm text-muted-foreground mb-3">You can request deletion of your personal data. We will delete:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>Account information (email, display name, profile)</li>
                <li>Authentication credentials</li>
                <li>Personal preferences and settings</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3"><strong>Exceptions (data we must keep):</strong></p>
              <ul className="list-disc pl-6 space-y-1 text-sm text-muted-foreground">
                <li>Transaction records (7 years for tax compliance)</li>
                <li>Completed bounties (90 days for dispute resolution)</li>
                <li>Moderation logs (2 years for audit trail)</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3"><strong>How to exercise:</strong> Email claimerbounty@gmail.com with "Delete My Account" | Processed within 30 days</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">4. Right to Restrict Processing (Article 18)</h3>
              <p className="text-sm text-muted-foreground mb-3">You can request we limit how we process your data in certain situations:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>If you contest data accuracy (restricted until verified)</li>
                <li>If processing is unlawful but you oppose deletion</li>
                <li>If we no longer need data but you require it for legal claims</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">During restriction, we can only store data (not use it).</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">5. Right to Data Portability (Article 20)</h3>
              <p className="text-sm text-muted-foreground mb-3">You can request your data in a machine-readable format for transfer to another service:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Format:</strong> JSON or CSV (structured, commonly used)</li>
                <li><strong>Includes:</strong> Account data, bounties, transactions, claims</li>
                <li><strong>Delivery:</strong> Via secure download link (expires in 7 days)</li>
                <li><strong>Direct Transfer:</strong> We can transfer directly to another controller if technically feasible</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3"><strong>How to exercise:</strong> Email claimerbounty@gmail.com with "Data Portability Request"</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">6. Right to Object (Article 21)</h3>
              <p className="text-sm text-muted-foreground mb-3">You can object to processing based on:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Legitimate Interests:</strong> We must stop unless we demonstrate compelling legitimate grounds</li>
                <li><strong>Direct Marketing:</strong> We must stop immediately (no exceptions)</li>
                <li><strong>Profiling:</strong> You can object to automated decision-making</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">BountyClaimer does NOT use automated profiling or decision-making.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">7. Right to Withdraw Consent (Article 7)</h3>
              <p className="text-sm text-muted-foreground mb-3">Where processing is based on consent, you can withdraw it at any time:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li>Analytics tracking (via browser cookie settings)</li>
                <li>Marketing communications (unsubscribe link in emails)</li>
                <li>Optional data processing (contact us to revoke)</li>
              </ul>
              <p className="text-sm text-muted-foreground mt-3">Withdrawal does NOT affect lawfulness of processing before withdrawal.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">8. Right to Lodge a Complaint (Article 77)</h3>
              <p className="text-sm text-muted-foreground mb-3">If you believe we've violated your data rights, you can complain to:</p>
              <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                <li><strong>Your local supervisory authority:</strong> <a href="https://edpb.europa.eu/about-edpb/about-edpb/members_en" target="_blank" className="text-primary hover:underline">EU Data Protection Authorities</a></li>
                <li><strong>Contact first:</strong> We recommend contacting us at claimerbounty@gmail.com before filing a complaint</li>
              </ul>

              <h3 className="text-xl font-semibold mt-6 mb-3">9. How to Exercise Your Rights</h3>
              <div className="bg-muted/30 p-4 rounded-lg space-y-3 text-sm">
                <p><strong>Email:</strong> claimerbounty@gmail.com</p>
                <p><strong>Subject Line:</strong> Specify which right you're exercising (e.g., "Right to Access Request")</p>
                <p><strong>Verification:</strong> We may request identity verification to prevent unauthorized disclosures</p>
                <p><strong>Response Time:</strong> 30 days maximum (may extend by 2 months for complex requests)</p>
                <p><strong>Cost:</strong> FREE (unless requests are manifestly unfounded or excessive)</p>
              </div>

              <h3 className="text-xl font-semibold mt-6 mb-3">10. Children's Data Rights</h3>
              <p className="text-sm text-muted-foreground">BountyClaimer is not intended for children under 18. If we discover we've collected data from a minor, we will delete it immediately. Parents/guardians can contact us to request deletion.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">11. Non-Discrimination</h3>
              <p className="text-sm text-muted-foreground">We will NOT discriminate against you for exercising your data rights. You will continue to receive the same quality of service and pricing.</p>

              <h3 className="text-xl font-semibold mt-6 mb-3">Contact</h3>
              <p className="text-sm text-muted-foreground">Data Protection Officer: <strong>claimerbounty@gmail.com</strong></p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Index;
